import { Link } from "@tanstack/react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { type SourceTriageItem, useSourceTriageItems } from "@/hooks/use-sources";

interface ExtractedItemsPreviewProps {
  sourceId: string;
  solutionId: string;
}

const STATUS_LABEL: Record<SourceTriageItem["effectiveStatus"], string> = {
  pending: "Pending",
  "in-scope": "In scope",
  "in-review": "In review",
  "out-of-scope": "Out of scope",
  deferred: "Deferred",
};

const TYPE_LABEL: Record<string, string> = {
  feature: "Feature",
  implied: "Implied",
  "tech-req": "Tech",
  "tech-cons": "Tech con",
  business: "Business",
  integration: "Integration",
  ui: "UI",
  copy: "Copy",
  process: "Process",
  nfr: "NFR",
  passing: "Passing",
};

export function ExtractedItemsPreview({ sourceId, solutionId }: ExtractedItemsPreviewProps) {
  const { data, isLoading, isError } = useSourceTriageItems(sourceId, true);

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-md border border-dashed border-[var(--color-border)] p-4 text-center text-sm text-muted-foreground">
        Couldn't load extracted items.
      </div>
    );
  }
  if (!data || data.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-[var(--color-border)] p-6 text-center text-sm text-muted-foreground">
        No scope items were extracted from this source.
      </div>
    );
  }

  return (
    <ol className="space-y-1.5">
      {data.map((item) => (
        <li key={item.id}>
          <Link
            to="/s/$solutionId/review/$itemId"
            params={{ solutionId, itemId: item.id }}
            className="block rounded-md border border-[var(--color-border)] px-3 py-2 text-sm hover:bg-[var(--color-muted)] transition-colors"
          >
            <div className="flex items-start gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums shrink-0 mt-0.5 w-20">
                {TYPE_LABEL[item.type] ?? item.type}
              </span>
              <span className="flex-1 text-foreground/90">{item.extracted}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0 mt-0.5">
                {STATUS_LABEL[item.effectiveStatus]}
              </span>
            </div>
          </Link>
        </li>
      ))}
    </ol>
  );
}
